import builder from '@builder.io/react';
import { Builder, GetContentOptions } from '@builder.io/sdk';

import { BUILDER_API_KEY } from '@/shared/env';
import { Article } from '@/types/Article';
import { Brand } from '@/types/Brand';
import { BuilderReference } from '@/types/Builder';
import { Category } from '@/types/Category';
import { Product } from '@/types/Product';

import { ALL_LOCALES, ContentHubUrlPrefix, Locale, LOCALE_LINKS } from './LangHelper';

builder.init(BUILDER_API_KEY);

const CATEGORY_ROOT = '/the-loop/';

export function mapBuilderObjectToArticle(object: any): Article | undefined {
  if (!object) {
    return;
  }

  const oData = object.data;

  const categories: Category[] = oData.categories?.map((category: any) =>
    mapBuilderObjectToCategory(category.category.value)
  );
  const author = oData?.author?.value?.data;

  const articleSlug = oData.url?.replace('/builder/', '') ?? '';

  const articleUrl =
    categories && categories.length
      ? `${categories[0].categoryUrl}/${articleSlug}`.replace(/\/{2,}/, '/')
      : null;

  return {
    id: object.id,
    title: oData.title ?? '',
    description: oData.description ?? '',
    categories: categories ?? null,
    author: author ?? null,

    leadImage: oData.leadImage ?? null,
    leadPortraitImage: oData.leadPortraitImage ?? null,
    leadImagePosition: oData.leadImagePosition ?? 'center',

    url: articleUrl ?? '',
    articleUrl: articleUrl ?? '',
    articleSlug,

    priority: oData.priority ?? 0,

    builderModel: object ?? null,

    seoTitle: oData.seoTitle ?? oData.title ?? '',
    seoDescription: oData.seoDescription ?? oData.description ?? '',

    lastUpdated: object.lastUpdated ?? null,
    overrideDate: oData.overrideDate ?? null,
    createdDate: object.createdDate ?? null,

    readingTime: oData.readingTime,
  };
}

export function mapBuilderObjectToCategory(item: any): Category {
  if (!item) {
    return {
      id: '',
      name: '',
      longName: null,

      description: '',
      categoryUrl: '',
      image: '',
      lastUpdated: '',
      seoTitle: '',
      seoDescription: '',
    };
  }

  let name;
  let category = item;

  if (item.data === undefined) {
    name = item.name ?? '';
  } else {
    category = item.data;
    name = item.name ?? item.data.name;
  }

  const categorySlug = categoryNameToUrlSlug(name);

  // TODO generalize this parser here
  return {
    id: item.id,
    name,
    longName: category.longName ?? null,

    categoryUrl: (CATEGORY_ROOT + categorySlug).replace(/\/{2,}/g, '/'),

    categorySlug,
    description: category.description || '',
    image: category.image || '',
    lastUpdated: item.lastUpdated || '',
    seoTitle: item.seoTitle || '',
    seoDescription: item.seoDescription || '',
    landscapeImage: item.landscapeImage || '',
  };
}

export function mapBuilderObjectToProduct(item: any): Product {
  if (!item) {
    return {
      id: '',
      name: '',
      description: '',
      leadImage: '',

      price: 0,
      priceUnit: '$',
    };
  }

  const oData = item.data;
  return {
    id: item.id,
    name: oData.name,
    description: oData.description,
    leadImage: oData.leadImage,
    brand: mapBuilderObjectToBrand(oData.brand),
    price: oData.price || 0,
    priceUnit: oData.priceUnit || '$',
    url: oData.url,
    lastUpdated: item.lastUpdated,
    installment: oData.installment,
  };
}

export function mapBuilderObjectToBrand(item: any): Brand {
  if (!item) {
    return {
      id: '',
      name: '',
      logo: '',
      lastUpdated: '',
    };
  }

  let oData = item.data;
  if (item['@type'] && item.value) {
    oData = item.value.data;
  }

  return {
    id: item.id,
    name: oData?.name,
    logo: oData?.logo,
    lastUpdated: item.lastUpdated,
  };
}

const categoryNameToUrlSlug = (name: string): string => {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^A-z]/g, '-')
    .trimEnd();
};

export const getAllArticles = async (
  locale?: Locale | Locale[],
  filters?: Partial<{ categoryIds: string[]; offset: number; limit: number; extra?: any }>,
  sort?: any,
  includeUnpublished = false,
  limitFields?: string
): Promise<Article[]> => {
  const getConfig: GetContentOptions = {
    query: {},
    options: {
      noTargeting: true,
      includeUnpublished,
    },
  };

  if (filters?.extra) {
    getConfig.query = filters?.extra;
  }

  if (locale) {
    getConfig.query.query = {
      $elemMatch: {
        property: {
          $eq: 'locale',
        },
        value: locale,
      },
    };
  }

  if (filters?.categoryIds?.length) {
    getConfig.query['data.categories'] = {
      $elemMatch: {
        'category.id': {
          $in: filters.categoryIds,
        },
      },
    };
  }

  if (sort && getConfig.options) {
    getConfig.options.sort = sort;
  }

  if (limitFields) {
    getConfig.fields = limitFields;
  }

  getConfig.limit = filters?.limit;

  if (getConfig.options) {
    if (typeof filters?.offset === 'string') {
      getConfig.options.offset = parseInt(filters?.offset ?? '0');
    } else {
      getConfig.options.offset = filters?.offset;
    }
  }

  const articles = await builder.getAll('article', getConfig);

  return articles
    .map((page) => {
      return mapBuilderObjectToArticle(page);
    })
    .filter((page) => page !== undefined) as Article[];
};

const getArticlePage = async (
  params: { url?: string; modelId?: string; locale?: Locale },
  includeUnpublished = false
) => {
  return getBuilderPage('article', params, includeUnpublished);
};

export const getBuilderPage = async (
  model: string,
  params: { url?: string; modelId?: string; locale?: Locale },
  includeUnpublished = false
) => {
  const query: any = {
    userAttributes: {},

    options: {
      includeUnpublished,
      noTargeting: false,
    },
  };

  if (params.url) {
    query.userAttributes.urlPath = params.url;

    if (params.locale) {
      query.userAttributes.locale = params.locale;
    } else {
      query.userAttributes.locale = ALL_LOCALES;
    }
  }

  if (params.modelId) {
    query.entry = params.modelId;
  }

  // TODO replace these string entries with constants
  const article = (await builder.get(model, query).toPromise()) || null;

  return article;
};

export const getArticlePageFromUrl = async (
  path = '',
  locale?: Locale,
  includeUnpublished = false
) => {
  return getArticlePage({ url: '/builder/' + path, locale }, includeUnpublished);
};

export const getArticleFromID = async (modelId: string, includeUnpublished = false) => {
  return getArticlePage({ modelId }, includeUnpublished);
};

export const getCategoryFromName = async (name: string) => {
  const slug = categoryNameToUrlSlug(name);

  const allPossibleCategories = await getAllCategories();
  for (const category of allPossibleCategories) {
    if (`${CATEGORY_ROOT}${slug}` === category.categoryUrl) {
      // TODO refactor check to come from a generic place
      return category;
    }
  }

  return null;
};

export const getCategoryFromID = async (modelId: string) => {
  const foundCategory = await getBuilderPage('category', { modelId }, true);

  if (foundCategory) {
    return mapBuilderObjectToCategory(foundCategory);
  }

  return null;
};

export const getArticlesByCategory = async ({
  locale,
  offset,
  limit = 9,
  categoryIds = [],
}: {
  locale: Locale;
  offset?: number;
  limit?: number;
  categoryIds?: string[];
}) => {
  return await getAllArticles(
    locale,
    { categoryIds, limit, offset },
    {
      'data.overrideDate': -1,
      createdDate: -1,
    }
  );
};

export const getLatestArticles = async ({
  locale,
  offset,
  limit = 4,
  categoryIds = [],
  excludeIds = [],
}: {
  locale: Locale;
  offset?: number;
  limit?: number;
  categoryIds?: string[];
  excludeIds?: string[];
}) => {
  let extra = {} as any;
  if (excludeIds?.length) {
    extra = {
      id: {
        $not: {
          $in: excludeIds,
        },
      },
    };
  }

  return getAllArticles(
    locale,
    {
      extra,
      categoryIds,
      offset,
      limit,
    },
    { 'data.overrideDate': -1, createdDate: -1 }
  );
};

let cachedCategoryList: Category[] = [];
export const getAllCategories = async () => {
  // TODO add caching mechanism to avoid loading this over and over again

  if (cachedCategoryList.length !== 0) {
    return cachedCategoryList;
  }

  const allCategory = await builder.getAll('category', {
    options: { noTargeting: true, includeUnpublished: false },
  });
  const categoriesData: Category[] = allCategory.map((item: any) =>
    mapBuilderObjectToCategory(item)
  );

  if (cachedCategoryList.length === 0) {
    cachedCategoryList = categoriesData;
  }
  return categoriesData;
};

export const getCategoriesFromIDs = async (categoryIds: string[] = []) => {
  const allCategory = await builder.getAll('category', {
    options: { noTargeting: true, includeUnpublished: false },
    query: {
      id: {
        $in: categoryIds,
      },
    },
  });
  const categoriesData: Category[] = allCategory.map((item: any) =>
    mapBuilderObjectToCategory(item)
  );

  return categoriesData;
};

export const getPageFromPageID = async (id: string) => {
  return getBuilderPage('page', { modelId: id }, true);
};

export const getArticleSearchResults = async (
  locale: Locale,
  keyword: string,
  limit = 25,
  offset = 0,
  onlyFields?: string
) => {
  return getAllArticles(
    locale,
    {
      extra: {
        $or: [
          {
            name: {
              $regex: keyword,
              $options: 'i',
            },
          },
          {
            description: {
              $regex: keyword,
              $options: 'i',
            },
          },
          {
            'data.title': {
              $regex: keyword,
              $options: 'i',
            },
          },
        ],
      },
      limit,
      offset,
    },
    undefined,
    false,
    onlyFields
  );
};

export const getArticleFromIDs = async (articleIds: string[] = []) => {
  const allCategory = await builder.getAll(BUILDER_MODEL.ARTICLE, {
    options: { noTargeting: true, includeUnpublished: false },
    query: {
      id: {
        $in: articleIds,
      },
    },
  });
  const articleData: Article[] = allCategory
    .map((item: any) => mapBuilderObjectToArticle(item))
    .filter((page) => page !== undefined) as Article[];

  return articleData;
};

export const getBrandFromIDs = async (brandIds: string[] = []) => {
  const allCategory = await builder.getAll(BUILDER_MODEL.BRAND, {
    options: { noTargeting: true, includeUnpublished: false },
    query: {
      id: {
        $in: brandIds,
      },
    },
  });
  const brandsData: Brand[] = allCategory.map((item: any) => mapBuilderObjectToBrand(item));

  return brandsData;
};

export const getProductFromIDs = async (productIds: string[] = []) => {
  const allCategory = await builder.getAll(BUILDER_MODEL.PRODUCT, {
    options: { noTargeting: true, includeUnpublished: false },
    query: {
      id: {
        $in: productIds,
      },
    },
  });
  const productsData: Product[] = allCategory.map((item: any) => mapBuilderObjectToProduct(item));

  return productsData;
};

/**
 * Function used for fetching paths for Static Path mapping
 *
 * @returns Promise
 */
export const getAllArticlePaths = async (): Promise<
  { lang: Locale; category: string; article: string; business: string }[]
> => {
  const pathArray: any[] = [];

  let currentOffset = 0;
  const fetchArticles = async (offset: number) => {
    const currentArticles = await getAllArticles(undefined, { offset, limit: 10 });

    currentArticles.map((page) => {
      const queryFilters = page.builderModel.query;

      // Check if page has lang
      if (queryFilters.length > 0 && queryFilters[1]?.property == 'locale') {
        queryFilters[1].value.forEach((lang: Locale) => {
          page.categories?.forEach((category: Category) => {
            if (category.categoryUrl) {
              const contentHubPath = LOCALE_LINKS[lang]?.contentHubRoot;

              pathArray.push({
                lang,
                category: category.categorySlug ?? '',
                article: page.articleSlug,
                business: contentHubPath ?? ContentHubUrlPrefix.TheLoop,
              });
            }
          });
        });
      }
    });

    if (currentArticles.length === 10) {
      await fetchArticles((currentOffset += 10));
    }
  };

  await fetchArticles(currentOffset);

  return pathArray;
};

export const getArticlesFromBuilderReference = async (
  articles?: { article?: BuilderReference<BUILDER_MODEL.ARTICLE> }[]
): Promise<Article[]> => {
  if (Builder.isEditing) {
    const articleIds = (articles || [])
      .map((article) => article?.article?.id)
      .filter(Boolean) as string[];
    return getArticleFromIDs(articleIds);
  }

  const categoryMap: { [key: string]: Category } = {};
  const categoryIds: string[] = [];
  (articles || []).forEach((article) => {
    article?.article?.value?.data?.categories.forEach((categoryRef: any) => {
      const category = categoryRef.category as BuilderReference<BUILDER_MODEL.CATEGORY>;
      if (category.value && category.value.id) {
        categoryMap[category.value.id] = category.value as Category;
      } else {
        categoryIds.push(category.id);
      }
    });
  });

  const categories = await getCategoriesFromIDs(categoryIds);
  categories.forEach((category) => {
    categoryMap[category.id] = category;
  });

  const updatedArticles = (articles || [])
    .map((article) => {
      const categories = article?.article?.value?.data?.categories.map((categoryRef: any) => {
        const category = (categoryRef as any).category as BuilderReference<BUILDER_MODEL.CATEGORY>;
        return {
          category: { value: categoryMap[category.id] },
        };
      });
      return mapBuilderObjectToArticle({
        ...(article?.article?.value || {}),
        data: {
          ...(article?.article?.value?.data || {}),
          categories,
        },
      });
    })
    .filter((page) => page !== undefined) as Article[];

  return updatedArticles;
};

export const getRelatedArticles = async (article: Article, locale: Locale): Promise<Article[]> => {
  if (!article) {
    return [];
  }

  const categoryIds = article.categories
    ? [...article.categories].map((item: Category) => {
        return item.id;
      })
    : [];

  const relatedPages = await builder.getAll('article', {
    options: { noTargeting: true },
    query: {
      id: {
        $ne: article.builderModel.id,
      },
      'data.categories': {
        $elemMatch: {
          'category.id': {
            $in: [categoryIds[0]],
          },
        },
      },
      query: {
        $elemMatch: {
          property: {
            $eq: 'locale',
          },
          value: locale,
        },
      },
    },
    limit: 3,
  });

  const relatedArticles: Article[] = relatedPages
    .map((page) => {
      return mapBuilderObjectToArticle(page);
    })
    .filter((page) => page !== undefined) as Article[];

  return relatedArticles;
};

export enum BUILDER_MODEL {
  ARTICLE = 'article',
  ARTICLE_AUTHOR = 'article-author',
  PAGE = 'page',
  CATEGORY = 'category',
  BRAND = 'brand',
  PRODUCT = 'product',
}

export type ImageResizeParameters = {
  width?: number;
  height?: number;
  quality?: number;
  format?: 'webp';
};

export const getResizedBuilderImage = (
  source: string | undefined,
  parameters?: ImageResizeParameters
) => {
  if (source && source.startsWith('https://cdn.builder.io/api/v1/image/')) {
    const queryArray: string[] = [];

    if (parameters) {
      parameters.quality = parameters.quality ?? 85;
      parameters.format = 'webp';

      const parameterKeys = Object.keys(parameters);
      parameterKeys.forEach((paramKey) => {
        const paramValue = parameters[paramKey as keyof ImageResizeParameters];
        if (paramValue) {
          queryArray.push(`${paramKey}=${paramValue}`);
        }
      });
    }

    return `${source}?${queryArray.join('&')}`;
  }

  return source;
};

export const getPlaceholderBuilderImage = (source: string | undefined) => {
  return getResizedBuilderImage(source, { width: 1, height: 1 });
};
